import * as React from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Popover,
  Slider,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";

import WorkspaceAvatarPicker from "../components/WorkspaceAvatarPicker";

/* ---------- Storage steps & helpers (identisk med SummaryEditor) ---------- */
const STORAGE_STEPS = [1, 2, 4, 10, 50, 100, 200, 300, 500, 1000];
const STORAGE_POS = [0, 6, 12, 24, 40, 55, 70, 82, 92, 100];

function indexFromGB(gb) {
  const i = STORAGE_STEPS.indexOf(gb ?? STORAGE_STEPS[0]);
  return i >= 0 ? i : 0;
}
function posFromGB(gb) { return STORAGE_POS[indexFromGB(gb)]; }
function nearestIndexFromPos(pos) {
  let best = 0, bestDist = Number.POSITIVE_INFINITY;
  for (let i = 0; i < STORAGE_POS.length; i++) {
    const d = Math.abs(pos - STORAGE_POS[i]);
    if (d < bestDist) { best = i; bestDist = d; }
  }
  return best;
}

/* ---------------- Små layout-hjälpare ---------------- */
function Section({ children, pb = 0 }) {
  return <Box sx={{ display: "block", pb }}>{children}</Box>;
}
function Row({ children, gap = 2, cols = "repeat(12, 1fr)" }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: cols }, gap }}>
      {children}
    </Box>
  );
}
function ListEditor({ label, items = [], placeholder = "Lägg till…", onChange }) {
  const [draft, setDraft] = React.useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    const next = Array.from(new Set([...(items || []), v]));
    onChange?.(next);
    setDraft("");
  };
  const remove = (i) => onChange?.(items.filter((_, idx) => idx !== i));
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          label={label}
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <Tooltip title="Lägg till">
          <span>
            <IconButton onClick={add} color="primary" disabled={!draft.trim()}>
              <AddRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {(items || []).map((m, i) => (
          <Chip
            key={`${m}-${i}`}
            label={m}
            onDelete={() => remove(i)}
            deleteIcon={<DeleteRoundedIcon />}
            sx={{ borderRadius: 999 }}
          />
        ))}
      </Stack>
    </Stack>
  );
}

/* ---------------- Component ---------------- */
export default function StepBoardName({ value, onNext, showActions = true }) {
  // lokalt state för inledande fält
  const [name, setName] = React.useState(value?.name ?? "");
  const [description, setDescription] = React.useState(value?.description ?? "");
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  // Popover för storage (öppnas via klick)
  const [storageAnchor, setStorageAnchor] = React.useState(null);

  // Lokalt editerings-state för lagring under drag
  const [editingStorageGB, setEditingStorageGB] = React.useState(
    value?.storageGB ?? STORAGE_STEPS[0]
  );

  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("lg"));

  // initiera default lagring vid behov
  React.useEffect(() => {
    if (value?.storageGB == null) {
      onNext?.({ ...value, storageGB: STORAGE_STEPS[0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sync in externa ändringar (om parent uppdaterar)
  React.useEffect(() => setName(value?.name ?? ""), [value?.name]);
  React.useEffect(() => setDescription(value?.description ?? ""), [value?.description]);
  React.useEffect(() => {
    // uppdatera lokalt editingvärde om popover är stängd
    if (!storageAnchor) setEditingStorageGB(value?.storageGB ?? STORAGE_STEPS[0]);
  }, [value?.storageGB, storageAnchor]);

  // central updater – slå ihop current props + lokalt state + patch och skicka upp
  const update = (patch) => {
    onNext?.({
      ...value,
      name,
      description,
      ...patch,
    });
  };

  // handlers för toppfälten (skickas upp “live”)
  const handleNameChange = (e) => { const v = e.target.value; setName(v); update({ name: v }); };
  const handleDescriptionChange = (e) => { const v = e.target.value; setDescription(v); update({ description: v }); };

  const openStorage = (el) => {
    // sätt lokalt editingvärde när vi öppnar
    setEditingStorageGB(value?.storageGB ?? STORAGE_STEPS[0]);
    setStorageAnchor(el);
  };
  const closeStorage = () => setStorageAnchor(null);

  const storageLabel = `${value?.storageGB ?? STORAGE_STEPS[0]} GB`;
  const submit = () => update({});

  return (
    <Stack spacing={3}>
      {/* Intro */}
      <Box>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Låt oss börja jobba tillsammans
        </Typography>
        <Typography color="text.secondary">
          Ge din arbetsyta ett namn, t.ex. <i>Dokumentlagring</i> eller <i>Marknadsföringsplan</i>.
        </Typography>
      </Box>

      {/* Grunddata – 53px-fält + ikon 53x53 */}
      <Section pb={1}>
        <Row cols="repeat(12, 1fr)" gap={2.25}>
          <TextField
            label="Arbetsytans namn"
            value={name}
            onChange={handleNameChange}
            fullWidth
            sx={{ gridColumn: { md: "span 5" }, "& .MuiInputBase-root": { height: 53 } }}
          />
          <TextField
            label="Arbetsytans beskrivning"
            value={description}
            onChange={handleDescriptionChange}
            fullWidth
            sx={{ gridColumn: { md: "span 5" }, "& .MuiInputBase-root": { height: 53 } }}
          />
          <Box sx={{ gridColumn: { md: "span 2" }, display: "flex", alignItems: "center" }}>
            <WorkspaceAvatarPicker
              value={{
                imageUrl: value?.imageUrl ?? null,
                iconId: value?.icon ?? null,
                color: value?.color ?? "#6a2135",
              }}
              onChange={(v) =>
                update({
                  imageUrl: v.imageUrl ?? null,
                  icon: v.iconId ?? null,
                  color: v.color ?? value?.color,
                })
              }
              noFrame
              compact
              hideHint
              height={53}
              width={53}
            />
          </Box>
        </Row>

        {/* Lagringsutrymme – text + penna; slider i Popover vid klick */}
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Arbetsytans lagringsutrymme är <b>{storageLabel}</b>
            </Typography>
            <IconButton
              aria-label="Ändra lagringsutrymme"
              size="small"
              onClick={(e) => openStorage(e.currentTarget)}
              sx={{ ml: 0.5 }}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          <Popover
            open={Boolean(storageAnchor)}
            anchorEl={storageAnchor}
            onClose={closeStorage}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{ paper: { sx: { px: 3, py: 3 } } }}  // lite högre + bredare padding
          >
            {(() => {
              const MARK_SET = compact ? [1, 2, 10, 100, 200, 1000] : STORAGE_STEPS;
              const marks = STORAGE_STEPS.map((gb, i) => ({
                value: STORAGE_POS[i],
                label: MARK_SET.includes(gb) ? String(gb) : "",
              }));

              // Viktigt: Använd LOKALT state under drag, inte props
              const sliderPos = posFromGB(editingStorageGB);

              return (
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ width: { xs: 420, sm: 560, md: 720 } }} // bredare
                >
                  <Slider
                    aria-label="Workspace storage"
                    value={sliderPos}
                    onChange={(_, pos) => {
                      const p = Array.isArray(pos) ? pos[0] : pos;
                      const next = STORAGE_STEPS[nearestIndexFromPos(p)];
                      setEditingStorageGB(next); // uppdatera lokalt, så drag flyter
                    }}
                    onChangeCommitted={() => {
                      // committa till parent först när användaren släpper
                      update({ storageGB: editingStorageGB });
                      closeStorage();
                    }}
                    min={0}
                    max={100}
                    step={null}
                    marks={marks}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(p) =>
                      `${STORAGE_STEPS[nearestIndexFromPos(Array.isArray(p) ? p[0] : p)]} GB`
                    }
                    getAriaValueText={(p) =>
                      `${STORAGE_STEPS[nearestIndexFromPos(Array.isArray(p) ? p[0] : p)]} GB`
                    }
                    sx={{
                      flexGrow: 1,
                      mt: 0.5,
                      "& .MuiSlider-markLabel": {
                        fontSize: 11,
                        transform: "translateY(6px)",
                        whiteSpace: "nowrap",
                        color: "text.secondary",
                      },
                      "& .MuiSlider-mark": { width: 2 },
                    }}
                  />

                  <Typography variant="body2" fontWeight={700} sx={{ minWidth: 80, textAlign: "right" }}>
                    {editingStorageGB} GB
                  </Typography>
                </Stack>
              );
            })()}
          </Popover>
        </Box>
      </Section>

      {/* --- Klickbar Divider som fäller ut Avancerat --- */}
      <Divider sx={{ my: 1.5 }}>
        <Button
          size="small"
          onClick={() => setShowAdvanced((v) => !v)}
          endIcon={showAdvanced ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
          sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary", px: 1.25, py: 0.25 }}
        >
          Avancerat
        </Button>
      </Divider>

      {/* AVANCERAT */}
      <Collapse in={showAdvanced} unmountOnExit>
        <Section>
          <Stack spacing={2.25}>
            <Row cols="repeat(12, 1fr)" gap={2.25}>
              <FormControl size="small" sx={{ gridColumn: { md: "span 6" } }}>
                <InputLabel>Arbetsytemall</InputLabel>
                <Select
                  label="Arbetsytemall"
                  value={value?.template || ""}
                  onChange={(e) => update({ template: e.target.value })}
                >
                  <MenuItem value="">Ingen</MenuItem>
                  <MenuItem value="projekt">Projekt</MenuItem>
                  <MenuItem value="crm">CRM</MenuItem>
                  <MenuItem value="ärenden">Ärenden</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ gridColumn: { md: "span 6" } }}>
                <InputLabel>Arbetsyteägare</InputLabel>
                <Select
                  label="Arbetsyteägare"
                  value={value?.owner || ""}
                  onChange={(e) => update({ owner: e.target.value })}
                >
                  <MenuItem value="">Välj…</MenuItem>
                  <MenuItem value="devops@kommunal.se">Devops Utvecklare</MenuItem>
                  <MenuItem value="you@company.se">Du</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ gridColumn: { md: "span 6" } }}>
                <InputLabel>Ordergrupp</InputLabel>
                <Select
                  label="Ordergrupp"
                  value={value?.orderGroup || ""}
                  onChange={(e) => update({ orderGroup: e.target.value })}
                >
                  <MenuItem value="">Ingen</MenuItem>
                  <MenuItem value="alpha">Alpha</MenuItem>
                  <MenuItem value="beta">Beta</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ gridColumn: { md: "span 6" } }}>
                <InputLabel>Dashboard</InputLabel>
                <Select
                  label="Dashboard"
                  value={value?.dashboard || ""}
                  onChange={(e) => update({ dashboard: e.target.value })}
                >
                  <MenuItem value="">Standard</MenuItem>
                  <MenuItem value="light">Light dashboard</MenuItem>
                  <MenuItem value="pro">Pro dashboard</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ gridColumn: { md: "span 6" } }}>
                <InputLabel>Nätverk</InputLabel>
                <Select
                  label="Nätverk"
                  value={value?.network || "Wilma network"}
                  onChange={(e) => update({ network: e.target.value })}
                >
                  <MenuItem value="Wilma network">Wilma network</MenuItem>
                  <MenuItem value="Extern">Extern</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ gridColumn: { md: "span 6" } }}>
                <InputLabel>Startsida</InputLabel>
                <Select
                  label="Startsida"
                  value={value?.startPage || ""}
                  onChange={(e) => update({ startPage: e.target.value })}
                >
                  <MenuItem value="">Översikt</MenuItem>
                  <MenuItem value="feed">Nyhetsflöde</MenuItem>
                  <MenuItem value="dashboard">Dashboard</MenuItem>
                </Select>
              </FormControl>
            </Row>

            <Row cols="repeat(12, 1fr)" gap={2.25}>
              <Box sx={{ gridColumn: { md: "span 6" } }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Menyer
                </Typography>
                <ListEditor
                  label="Ny meny"
                  items={value?.menus || []}
                  onChange={(list) => update({ menus: list })}
                />
              </Box>

              <Box sx={{ gridColumn: { md: "span 6" } }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Roller
                </Typography>
                <ListEditor
                  label="Ny roll"
                  items={value?.roles || []}
                  onChange={(list) => update({ roles: list })}
                />
              </Box>
            </Row>
          </Stack>
        </Section>
      </Collapse>

      {showActions && (
        <Box>
          <Button variant="contained" size="large" onClick={submit} disabled={!name.trim()}>
            Nästa
          </Button>
        </Box>
      )}
    </Stack>
  );
}
